// ─────────────────────────────────────────────────────────────────────────────
// CustomerEmailsEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for the customerEmails slice.
// Responsibilities:
//   - Load per-customer email registry from /v1/company-emails
//   - Create / Update / Delete with typed error handling (409 duplicate, 404 not-found)
//   - Map HTTP 404 on Update/Delete to a friendly "row no longer exists"
//     toast + re-dispatch LoadCustomerEmails so the UI re-syncs with reality
//     (Phase 21 D-14: ERP rows return 404 by design to prevent ID-spraying)
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { CustomerEmailService } from '../services/customer-email.service';
import { ICompanyEmail, IStructuredApiError, STRUCTURED_ERROR_CODES } from '../models/customer-email.models';
import * as customerEmailsActions from './customer-emails.actions';

@Injectable()
export class CustomerEmailsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private customerEmailService: CustomerEmailService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load customer emails ─────────────────────────────────────────────────

  LoadCustomerEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerEmailsActions.LoadCustomerEmails),
      switchMap((action) =>
        this.customerEmailService.list(action.customerId).pipe(
          map((response: APIResponse<ICompanyEmail>) =>
            customerEmailsActions.LoadCustomerEmailsSuccess({
              customerId: action.customerId,
              response,
            }),
          ),
          catchError((err: IStructuredApiError) => of(customerEmailsActions.LoadCustomerEmailsError({ error: err?.message || 'Load failed' }))),
        ),
      ),
    ),
  );

  // ── Create customer email ────────────────────────────────────────────────

  CreateCustomerEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerEmailsActions.CreateCustomerEmail),
      mergeMap((action) =>
        this.customerEmailService.create(action.payload).pipe(
          map((response: APIResponse<ICompanyEmail>) => {
            const email = response.object ?? response.data[0];
            return customerEmailsActions.CreateCustomerEmailSuccess({ email });
          }),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_create_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            return of(customerEmailsActions.CreateCustomerEmailError({ error }));
          }),
        ),
      ),
    ),
  );

  // ── Update customer email ────────────────────────────────────────────────

  UpdateCustomerEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerEmailsActions.UpdateCustomerEmail),
      mergeMap((action) =>
        this.customerEmailService.update(action.id, action.payload).pipe(
          map((response: APIResponse<ICompanyEmail>) => {
            const email = response.object ?? response.data[0];
            return customerEmailsActions.UpdateCustomerEmailSuccess({ email });
          }),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_update_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
              this.store.dispatch(customerEmailsActions.LoadCustomerEmails({ customerId: action.customerId }));
            }
            return of(customerEmailsActions.UpdateCustomerEmailError({ error, customerId: action.customerId }));
          }),
        ),
      ),
    ),
  );

  // ── Delete customer email ────────────────────────────────────────────────

  DeleteCustomerEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerEmailsActions.DeleteCustomerEmail),
      mergeMap((action) =>
        this.customerEmailService.remove(action.id).pipe(
          map(() =>
            customerEmailsActions.DeleteCustomerEmailSuccess({
              id: action.id,
              customerId: action.customerId,
            }),
          ),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_delete_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
              this.store.dispatch(customerEmailsActions.LoadCustomerEmails({ customerId: action.customerId }));
            }
            return of(customerEmailsActions.DeleteCustomerEmailError({ error, customerId: action.customerId }));
          }),
        ),
      ),
    ),
  );

  // ── Helpers ──────────────────────────────────────────────────────────────

  private resolveToastMessage(error: IStructuredApiError): string {
    if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
      return this.translateService.instant('customers.contacts_tab.email_not_found_error');
    }
    if (error?.code === STRUCTURED_ERROR_CODES.DUPLICATE) {
      return this.translateService.instant('customers.contacts_tab.email_duplicate_error');
    }
    return error?.message || this.translateService.instant('customers.contacts_tab.email_server_error');
  }
}
